import { hashKey, type QueryClient, type QueryKey } from '@tanstack/react-query';

/**
 * One Try again press runs every waiting region's handler in the same turn, and several regions
 * can wait on ONE query (the dashboard's three charts). Each restart abandons the request before
 * it, so N handlers would send N requests. Calls for the same key in the same turn share one.
 */
const sameTurn = new WeakMap<QueryClient, Map<string, Promise<void>>>();
function oncePerTurn(queryClient: QueryClient, queryKey: QueryKey, run: () => Promise<void>): Promise<void> {
  let turn = sameTurn.get(queryClient);
  if (!turn) { turn = new Map(); sameTurn.set(queryClient, turn); }
  const id = hashKey(queryKey);
  const running = turn.get(id);
  if (running) return running;
  const started = run();
  turn.set(id, started);
  queueMicrotask(() => { turn.delete(id); });
  return started;
}

/**
 * Re-run a query's first load from scratch (a waiting region's Try again, #1561).
 *
 * `query.refetch()` is NOT that on a query that has no data yet: TanStack joins
 * the request already in flight (`cancelRefetch` only applies once data exists),
 * so Try again at 15 s on a hung first load re-sent nothing. `resetQueries`
 * abandons the in-flight request (its late answer is dropped) and starts a new
 * one, in one notification: observers go pending/fetching to pending/fetching,
 * so a screen never flashes its empty state in between.
 */
export function restartQuery(queryClient: QueryClient, queryKey: QueryKey): Promise<void> {
  return oncePerTurn(queryClient, queryKey, () => queryClient.resetQueries({ queryKey, exact: true }));
}

/**
 * Try again for a region that may or may not hold data yet (#1561-d).
 *
 * No data: the first load, so `restartQuery` (a bare refetch would join the hung
 * request). Data present (a failed refetch or next page under visible rows): a
 * plain refetch, which cancels the in-flight request itself and keeps the rows on
 * screen; resetting would blank them.
 */
export function retryQuery(queryClient: QueryClient, queryKey: QueryKey): Promise<void> {
  if (queryClient.getQueryState(queryKey)?.data === undefined) return restartQuery(queryClient, queryKey);
  return oncePerTurn(queryClient, queryKey, () => queryClient.refetchQueries({ queryKey, exact: true }));
}
